// Browser detection utility for mobile browser compatibility
// Detects specific mobile browsers and provides browser-specific handling

/**
 * Detects mobile browser type and capabilities
 * @returns {Object} Browser information object
 */
export function detectMobileBrowser() {
    const ua = navigator.userAgent || navigator.vendor || window.opera || '';
    
    // Detect iOS Safari
    const isIOSSafari = /iPad|iPhone|iPod/.test(ua) && !window.MSStream && !/CriOS|FxiOS|OPiOS/.test(ua);
    
    // Detect Android Chrome
    const isAndroidChrome = /Android/.test(ua) && /Chrome/.test(ua) && !/EdgA|Edg/.test(ua);
    
    // Detect Samsung Internet
    const isSamsungInternet = /SamsungBrowser/.test(ua);
    
    // Detect in-app browsers (Facebook, Instagram, etc.)
    const isInAppBrowser = /FBAN|FBAV|Instagram|Line|LinkedInApp|Twitter|WhatsApp|Snapchat/.test(ua);
    
    // Detect mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    
    // Detect iOS version
    let iosVersion = null;
    if (/iPhone|iPad|iPod/.test(ua)) {
        const match = ua.match(/OS (\d+)_(\d+)_?(\d+)?/);
        if (match) {
            iosVersion = {
                major: parseInt(match[1], 10),
                minor: parseInt(match[2], 10),
                patch: match[3] ? parseInt(match[3], 10) : 0
            };
        }
    }
    
    // Detect Android version
    let androidVersion = null;
    if (/Android/.test(ua)) {
        const match = ua.match(/Android (\d+)\.(\d+)/);
        if (match) {
            androidVersion = {
                major: parseInt(match[1], 10),
                minor: parseInt(match[2], 10)
            };
        }
    }
    
    return {
        userAgent: ua,
        isMobile: isMobile,
        isIOSSafari: isIOSSafari,
        isAndroidChrome: isAndroidChrome,
        isSamsungInternet: isSamsungInternet,
        isInAppBrowser: isInAppBrowser,
        iosVersion: iosVersion,
        androidVersion: androidVersion,
        browserType: getBrowserType(isIOSSafari, isAndroidChrome, isSamsungInternet, isInAppBrowser, isMobile)
    };
}

/**
 * Gets browser type string for logging
 */
function getBrowserType(isIOSSafari, isAndroidChrome, isSamsungInternet, isInAppBrowser, isMobile) {
    if (isInAppBrowser) {
        return 'in-app-browser';
    }
    if (isIOSSafari) {
        return 'ios-safari';
    }
    if (isSamsungInternet) {
        return 'samsung-internet';
    }
    if (isAndroidChrome) {
        return 'android-chrome';
    }
    if (isMobile) {
        return 'mobile-other';
    }
    return 'desktop';
}

/**
 * Checks if browser supports sessionStorage reliably
 * Some browsers in private mode or in-app browsers have issues
 */
export function isSessionStorageReliable() {
    try {
        const test = '__sessionStorage_reliability_test__';
        window.sessionStorage.setItem(test, test);
        const retrieved = window.sessionStorage.getItem(test);
        window.sessionStorage.removeItem(test);
        return retrieved === test;
    } catch (_) {
        return false;
    }
}

/**
 * Gets recommended redirect method for current browser
 * @returns {string} 'replace' or 'href'
 */
export function getRecommendedRedirectMethod() {
    const browser = detectMobileBrowser();
    
    // Use replace() for mobile browsers to prevent back-button issues
    if (browser.isMobile) {
        return 'replace';
    }
    
    // Default to href for desktop
    return 'href';
}

